import type { CaptureResult, EventName } from "posthog-js";

// Developer and internal team emails that should be excluded from analytics
const DEVELOPER_EMAILS = [
  "zapifypro@gmail.com",
  "aryan23062001@gmail.com",
  "dhar.ruchika@gmail.com",
  "prakash@mojowebtech.com",
  "prashant@mojowebtech.com",
  "maruf@mojowebtech.com",
  "aakash.pardeshi000@gmail.com",
  "prashantmanwan@gmail.com",
  "vinayamin1997@gmail.com",
  "aryan@mojowebtech.com",
  "hardik@mojowebtech.com",
  "sauravdhar10@gmail.com",
  "me@saurabhdhar.com",
];

const LANDING_PAGE_SESSION_SAMPLE_RATE = 0.1;
const LOCALIZED_LANDING_PAGE_PATHS = new Set(["/es", "/it", "/pt", "/tr", "/fr", "/de"]);
const ERROR_EVENT_NAME = "$exception";
const SESSION_REPLAY_EVENT_NAME = "$snapshot";

type QueuedPostHogEvent =
  | { type: "track"; name: string; properties?: Record<string, any> }
  | { type: "identify"; userId: string; email?: string; name?: string };

type SessionRecordingDecisionArgs = {
  eventName: string;
  pathname: string;
  sampleKey?: string;
  sampleRate?: number;
};

type PublicPostHogFilterOptions = {
  hasForcedErrorRecording?: boolean;
};

// Event queue for events fired before PostHog loads
let eventQueue: QueuedPostHogEvent[] = [];
let posthogInstance: typeof import("posthog-js").default | null = null;
let isInitializing = false;
let hasForcedErrorRecording = false;

function normalizePathname(pathname: string): string {
  const pathOnly = pathname.split(/[?#]/)[0] || "/";

  if (pathOnly === "/") return "/";

  return pathOnly.replace(/\/+$/, "");
}

export function isLandingPagePath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname);

  return normalizedPathname === "/" || LOCALIZED_LANDING_PAGE_PATHS.has(normalizedPathname);
}

function deterministicSampleRatio(sampleKey: string): number {
  let hashA = 0xdeadbeef ^ sampleKey.length;
  let hashB = 0x41c6ce57 ^ sampleKey.length;

  for (let index = 0; index < sampleKey.length; index += 1) {
    const charCode = sampleKey.charCodeAt(index);
    hashA = Math.imul(hashA ^ charCode, 2654435761);
    hashB = Math.imul(hashB ^ charCode, 1597334677);
  }

  hashA =
    Math.imul(hashA ^ (hashA >>> 16), 2246822507) ^
    Math.imul(hashB ^ (hashB >>> 13), 3266489909);
  hashB =
    Math.imul(hashB ^ (hashB >>> 16), 2246822507) ^
    Math.imul(hashA ^ (hashA >>> 13), 3266489909);

  return (hashB >>> 0) / 2 ** 32;
}

export function shouldRecordLandingPageSession(
  pathname: string,
  sampleKey: string,
  sampleRate = LANDING_PAGE_SESSION_SAMPLE_RATE,
): boolean {
  return isLandingPagePath(pathname) && deterministicSampleRatio(sampleKey) < sampleRate;
}

export function shouldRecordPostHogSession({
  eventName,
  pathname,
  sampleKey,
  sampleRate = LANDING_PAGE_SESSION_SAMPLE_RATE,
}: SessionRecordingDecisionArgs): boolean {
  if (eventName === ERROR_EVENT_NAME) return true;
  if (!sampleKey) return false;

  return shouldRecordLandingPageSession(pathname, sampleKey, sampleRate);
}

export function filterPublicPostHogCapture(
  capture: CaptureResult | null,
  pathname: string,
  options: PublicPostHogFilterOptions = {},
): CaptureResult | null {
  if (!capture) return null;
  if (capture.event === ERROR_EVENT_NAME || isLandingPagePath(pathname)) return capture;
  if (options.hasForcedErrorRecording && capture.event === SESSION_REPLAY_EVENT_NAME) {
    return capture;
  }

  return null;
}

function getCurrentPathname(): string {
  return window.location.pathname;
}

export const initPostHog = () => {
  if (typeof window === "undefined" || isInitializing || posthogInstance) return;

  const isAnalyticsOff = document.cookie.split("; ").some((c) => c === "zapify_analytics_off=1");
  if (isAnalyticsOff) return;

  isInitializing = true;

  import("posthog-js").then(({ default: posthog }) => {
    const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

    if (!posthogKey || !posthogHost) {
      console.warn("PostHog environment variables not found");
      isInitializing = false;
      return;
    }

    posthog.init(posthogKey, {
      api_host: posthogHost,
      autocapture: false,
      before_send: (capture) => {
        const pathname = getCurrentPathname();
        const filteredCapture = filterPublicPostHogCapture(capture, pathname, {
          hasForcedErrorRecording,
        });

        if (
          filteredCapture &&
          shouldRecordPostHogSession({
            eventName: filteredCapture.event,
            pathname,
            sampleKey: posthog.get_session_id() || posthog.get_distinct_id(),
          })
        ) {
          if (filteredCapture.event === ERROR_EVENT_NAME) {
            hasForcedErrorRecording = true;
          }

          posthog.startSessionRecording(true);
        }

        return filteredCapture;
      },
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      capture_pageleave: false,
      capture_pageview: false,
      defaults: "2025-05-24",
      disable_session_recording: true,
      enable_recording_console_log: true,
      person_profiles: "identified_only",
      session_recording: {
        maskAllInputs: true,
      },
      loaded: (loadedPosthog) => {
        if (
          shouldRecordLandingPageSession(
            getCurrentPathname(),
            loadedPosthog.get_session_id() || loadedPosthog.get_distinct_id(),
          )
        ) {
          loadedPosthog.startSessionRecording({ sampling: true });
        }
      },
    });

    posthogInstance = posthog;

    // Opt out internal team members from tracking
    const userEmail = localStorage.getItem("user_email");
    if (userEmail && DEVELOPER_EMAILS.includes(userEmail)) {
      posthog.opt_out_capturing();
    }

    // Flush queued events
    for (const event of eventQueue) {
      if (event.type === "track") {
        posthog.capture(event.name as EventName, event.properties);
      } else if (event.type === "identify") {
        if (event.email && DEVELOPER_EMAILS.includes(event.email)) {
          posthog.opt_out_capturing();
        } else {
          posthog.identify(event.userId, { email: event.email, name: event.name });
        }
      }
    }
    eventQueue = [];
  });
};

export const identifyUser = (userId: string, email?: string, name?: string) => {
  if (typeof window === "undefined") return;

  if (posthogInstance) {
    if (email && DEVELOPER_EMAILS.includes(email)) {
      posthogInstance.opt_out_capturing();
      return;
    }
    posthogInstance.identify(userId, { email, name });
    posthogInstance.capture("user_identified", { email, name });
  } else {
    eventQueue.push({ type: "identify", userId, email, name });
  }
};

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window === "undefined") return;

  if (posthogInstance) {
    posthogInstance.capture(eventName as EventName, properties);
  } else {
    eventQueue.push({ type: "track", name: eventName, properties });
  }
};
